import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, ZoomIn, ZoomOut } from '@mui/icons-material';
import tokens from '../../../theme/tokens';
import { useLanguage } from '../../../localization';

const ImageCropper = ({ 
  open, 
  onClose, 
  imageFile, 
  onCropComplete,
  cropSize = 200 // Size of the crop area in pixels
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageUrl, setImageUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(null);
  
  const { t } = useLanguage();
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // Load image when component opens
  useEffect(() => {
    if (open && imageFile) {
      setImageLoading(true);
      setError(null);
      
      const url = URL.createObjectURL(imageFile);
      setImageUrl(url);
      
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [open, imageFile]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setImageLoaded(false);
      setImageLoading(false);
      setError(null);
    }
  }, [open]);

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      
      setImageDimensions({ width: naturalWidth, height: naturalHeight });
      
      // Calculate minimum scale that allows the entire image to fit within the crop area
      const minDimension = Math.min(naturalWidth, naturalHeight);
      const maxDimension = Math.max(naturalWidth, naturalHeight);
      
      // Allow zooming out to see the entire image, even if it's much larger than crop area
      const minScale = Math.min(cropSize / maxDimension, 0.1); // Allow very small zoom
      
      // Set initial scale to show most of the image while ensuring it covers the crop area
      const coverScale = cropSize / minDimension; // Scale needed to cover crop area
      const fitScale = cropSize / maxDimension;   // Scale to fit entire image
      
      // Start with a scale that's between fit and cover, closer to fit for large images
      const initialScale = Math.max(minScale, Math.min(coverScale, fitScale * 1.2));
      
      setScale(initialScale);
      
      // Center the image initially
      setPosition({ x: 0, y: 0 });
      
      setImageLoaded(true);
      setImageLoading(false);
    }
  }, [cropSize]);

  const handleImageError = useCallback(() => {
    setError(t('imageCropper.failedToLoad'));
    setImageLoading(false);
    setImageLoaded(false);
  }, []);

  // Helper function to get coordinates from mouse or touch event
  const getEventCoordinates = useCallback((e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  }, []);

  const handleStart = useCallback((e) => {
    if (!imageLoaded) return;
    
    const coords = getEventCoordinates(e);
    setIsDragging(true);
    setDragStart({
      x: coords.x - position.x,
      y: coords.y - position.y
    });
    e.preventDefault();
  }, [imageLoaded, position, getEventCoordinates]);

  const handleMove = useCallback((e) => {
    if (!isDragging || !imageLoaded) return;
    
    const coords = getEventCoordinates(e);
    const newX = coords.x - dragStart.x;
    const newY = coords.y - dragStart.y;
    
    // Calculate bounds to prevent dragging too far
    const scaledWidth = imageDimensions.width * scale;
    const scaledHeight = imageDimensions.height * scale;
    
    const maxX = Math.max(0, (scaledWidth - cropSize) / 2);
    const maxY = Math.max(0, (scaledHeight - cropSize) / 2);
    
    setPosition({
      x: Math.max(-maxX, Math.min(maxX, newX)),
      y: Math.max(-maxY, Math.min(maxY, newY))
    });
    
    e.preventDefault();
  }, [isDragging, imageLoaded, dragStart, scale, imageDimensions, cropSize, getEventCoordinates]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add event listeners for mouse and touch events
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      
      // Touch events
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  const handleScaleChange = (event, newValue) => {
    setScale(newValue);
    
    // Adjust position to keep the image centered when scaling
    const scaledWidth = imageDimensions.width * newValue;
    const scaledHeight = imageDimensions.height * newValue;
    
    const maxX = Math.max(0, (scaledWidth - cropSize) / 2);
    const maxY = Math.max(0, (scaledHeight - cropSize) / 2);
    
    setPosition(prevPosition => ({
      x: Math.max(-maxX, Math.min(maxX, prevPosition.x)),
      y: Math.max(-maxY, Math.min(maxY, prevPosition.y))
    }));
  };

  const cropImage = useCallback(() => {
    if (!imageRef.current || !canvasRef.current) return null;
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = imageRef.current;
      
      // Set canvas size to crop size
      canvas.width = cropSize;
      canvas.height = cropSize;
      
      // Clear canvas
      ctx.clearRect(0, 0, cropSize, cropSize);
      
      // Create circular clipping path
      ctx.save();
      ctx.beginPath();
      ctx.arc(cropSize / 2, cropSize / 2, cropSize / 2, 0, Math.PI * 2);
      ctx.clip();
      
      // Calculate source position and size
      const scaledWidth = imageDimensions.width * scale;
      const scaledHeight = imageDimensions.height * scale;
      
      // Calculate the position to draw the image
      const drawX = (cropSize - scaledWidth) / 2 + position.x;
      const drawY = (cropSize - scaledHeight) / 2 + position.y;
      
      // Draw the image
      ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);
      ctx.restore();
      
      // Convert to blob and return as data URL
      return canvas.toDataURL('image/jpeg', 0.9);
    } catch (error) {
      console.error('Error cropping image:', error);
      setError(t('imageCropper.error'));
      return null;
    }
  }, [scale, position, imageDimensions, cropSize]);

  const handleConfirm = () => {
    const croppedImageUrl = cropImage();
    if (croppedImageUrl && onCropComplete) {
      onCropComplete(croppedImageUrl);
      onClose();
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6">{t('imageCropper.title')}</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ textAlign: 'center', py: 2 }}>
        {error ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('imageCropper.instruction')}
          </Typography>
        )}
        
        {/* Crop Area */}
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: cropSize,
            height: cropSize,
            mx: 'auto',
            mb: 3,
            border: `2px solid ${tokens.colors.primary.main}`,
            borderRadius: '50%',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
            backgroundColor: '#f5f5f5',
            touchAction: 'none', // Prevent default touch behaviors
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseDown={handleStart}
          onTouchStart={handleStart}
        >
          {imageLoading && (
            <CircularProgress size={40} />
          )}
          
          {imageUrl && !error && (
            <>
              <img
                ref={imageRef}
                src={imageUrl}
                alt="Crop preview"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${scale})`,
                  transformOrigin: 'center',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  touchAction: 'none',
                  display: imageLoaded ? 'block' : 'none',
                }}
                draggable={false}
              />
              
              {/* Overlay to show crop boundary */}
              {imageLoaded && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    border: `2px dashed rgba(255, 255, 255, 0.7)`,
                    borderRadius: '50%',
                    pointerEvents: 'none'
                  }}
                />
              )}
            </>
          )}
        </Box>
        
        {/* Zoom Controls */}
        {imageLoaded && !error && (
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <ZoomOut />
              <Slider
                value={scale}
                onChange={handleScaleChange}
                min={Math.min(cropSize / Math.max(imageDimensions.width, imageDimensions.height), 0.1)}
                max={5}
                step={0.05}
                sx={{ flex: 1 }}
                size="small"
              />
              <ZoomIn />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {t('imageCropper.zoom')}: {Math.round(scale * 100)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('imageCropper.reposition')}
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* Hidden canvas for cropping */}
        <canvas
          ref={canvasRef}
          style={{ display: 'none' }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {t('actions.cancel')}
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={!imageLoaded || !!error}
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper; 